// src/pages/LandingNavbar.js
import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Menu, X, Languages, ChevronDown } from "lucide-react";
import "./LandingNavbar.css";
import olympeakLogo from "../../assets/olympeak_logo_new.png";
import DropdownMenu from "../../components/DropdownMenu/DropdownMenu";

function normalizeLang(v) {
  const s = String(v || "").trim().toLowerCase();
  return s === "sk" ? "sk" : "en";
}

export default function LandingNavbar() {
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  const mobileNavRef = useRef(null);
  const langBtnRef = useRef(null);
  const [flyoutStyle, setFlyoutStyle] = useState(null);

  const currentLang = normalizeLang(i18n.language);

  const currentLangLabel =
    currentLang === "sk"
      ? t("landing.lang.sk", "Slovak")
      : t("landing.lang.en", "English");

    async function setPublicLang(next) {
      const lang = normalizeLang(next);

      try {
        localStorage.setItem("app_lang_v1", lang);
      } catch (e) {
        // ignore storage errors (private mode etc.)
      }

      if (i18n.language !== lang) {
        await i18n.changeLanguage(lang);
      }
    }

  function closeAll() {
    setLangOpen(false);
    setMenuOpen(false);
  }


  // ✅ compute submenu position (stacked below the Language row)
  useLayoutEffect(() => {
    if (!menuOpen || !langOpen) return;
    if (!mobileNavRef.current || !langBtnRef.current) return;

    const navRect = mobileNavRef.current.getBoundingClientRect();
    const btnRect = langBtnRef.current.getBoundingClientRect();

    const cs = getComputedStyle(document.documentElement);
    const offsetY = parseFloat(cs.getPropertyValue("--menu-offset-y")) || 12;

    const top = (btnRect.bottom - navRect.top) + offsetY;

    setFlyoutStyle({
      top: `${top}px`,
      right: `0px`,
      minWidth: `190px`,
    });
  }, [menuOpen, langOpen]);

  return (
    <header className="lp-header">
      <div className="lp-header-inner">
        <div className="lp-brand">
          <img src={olympeakLogo} alt={t("landing.brandAlt")} className="lp-logo-sm" />
          <span className="lp-title">Olympeak</span>
        </div>

        <div className="lp-header-ctas">
          <div className="lp-mobile-nav" ref={mobileNavRef}>
            <a href="/auth?mode=register">
              <button className="btn primary lp-cta-getstarted" type="button">
                {t("landing.cta.getStarted")} <ArrowRight size={16} />
              </button>
            </a>

            <button
              type="button"
              className="app-icon-btn"
              aria-label={menuOpen ? t("landing.menu.close") : t("landing.menu.open")}
              aria-expanded={menuOpen}
              onClick={(e) => {
                e.stopPropagation(); // ✅ prevents the outside-click handler from firing first
                setMenuOpen((v) => {
                  const next = !v;
                  if (!next) setLangOpen(false);
                  return next;
                });
              }}
            >
              {menuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>

            {/* MAIN MENU */}
            <DropdownMenu
              open={menuOpen}
              onClose={closeAll}
              ariaLabel={t("landing.menu.mobileAria")}
              align="right"
              variant="dropdown"
            >
              <a className="btn ghost" href="/about" role="menuitem" onClick={closeAll}>
                {t("landing.nav.about")}
              </a>
              <a className="btn ghost" href="/terms" role="menuitem" onClick={closeAll}>
                {t("landing.nav.terms")}
              </a>

              <div className="ui-menu-sep" />

              {/* ✅ Lean LanguageSwitcher trigger (opens YOUR secondary dropdown) */}
                <button
                  ref={langBtnRef}
                  type="button"
                  role="menuitem"
                  className="btn ghost lp-lang-trigger"
                  aria-label={t("landing.lang.label", "Language")}
                  aria-expanded={langOpen}
                  onClick={(e) => {
                    e.stopPropagation(); // ✅ prevents the dropdown "outside click" handler from firing
                    setLangOpen((v) => !v);
                  }}
                >
                  <span className="lp-lang-icon" aria-hidden="true">
                    <Languages size={16} />
                  </span>

                  <span className="lp-lang-label">
                    {currentLangLabel}
                  </span>

                  <span className="lp-lang-icon" aria-hidden="true">
                    <ChevronDown size={16} className={`lp-lang-chev ${langOpen ? "is-open" : ""}`} />
                  </span>
                </button>
            </DropdownMenu>

            {/* SUBMENU (your existing one) */}
            <DropdownMenu
              open={menuOpen && langOpen}
              onClose={closeAll}
              ariaLabel={t("landing.lang.label", "Language")}
              align="right"
              variant="dropdown"
              className="lp-lang-flyout"
              style={flyoutStyle}
            >
              <button
                className="btn ghost"
                type="button"
                role="menuitem"
                onClick={async (e) => {
                  e.stopPropagation();
                  await setPublicLang("en");
                  closeAll();
                }}
              >
                {t("landing.lang.en", "English")}
              </button>

              <button
                className="btn ghost"
                type="button"
                role="menuitem"
                onClick={async (e) => {
                  e.stopPropagation();
                  await setPublicLang("sk");
                  closeAll();
                }}
              >
                {t("landing.lang.sk", "Slovak")}
              </button>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
}